export const CACHE_MAX_AGE = Infinity;
export const CACHE_STALE_TIME = 60_000;
const PREFIX = "group-query-cache:";
const CONTENT_TYPES = new Set(["groups", "group", "classes", "progress", "members", "feed", "dm-list", "dm-messages"]);
// Serialize writes and cleanup so an old account's queued save cannot undo logout.
let pending = Promise.resolve();
const enqueue = action => {
  const result = pending.then(action).catch(() => undefined);
  pending = result;
  return result;
};

export function createAccountPersistence(userId, storage, legacyStores = []) {
  const key = `${PREFIX}${userId || "public"}`;
  const allowed = query => query.queryKey?.[0] === "group-directory" || (Boolean(userId) && query.queryKey?.[0] === "account" && query.queryKey[1] === userId && CONTENT_TYPES.has(query.queryKey[2]));
  let active = true;
  const legacyRemove = all => legacyStores.forEach(legacy => {
    try {
      for (let i = legacy.length - 1; i >= 0; i--) {
        const storedKey = legacy.key(i);
        if (storedKey?.startsWith(PREFIX) && (all || storedKey !== key)) legacy.removeItem(storedKey);
      }
    } catch { /* Browser storage may be blocked. */ }
  });
  legacyRemove(!userId);
  // Discard other accounts even when login changed outside this React tree.
  enqueue(async () => {
    if (!active) return;
    const keys = storage.keys ? await storage.keys() : Array.from({ length: storage.length }, (_, i) => storage.key(i));
    for (const storedKey of keys) {
      if (storedKey?.startsWith(PREFIX) && storedKey !== key) await storage.removeItem(storedKey);
    }
  });
  const persister = {
    persistClient: value => enqueue(async () => {
      if (active) await storage.setItem(key, JSON.stringify(value));
    }),
    restoreClient: () => enqueue(async () => {
      if (!active) return;
      let raw = await storage.getItem(key);
      if (!raw) {
        for (const legacy of legacyStores) {
          try { raw = legacy.getItem(key); } catch { /* Try the next legacy store. */ }
          if (raw) break;
        }
      }
      if (!raw) return;
      try {
        const value = JSON.parse(raw);
        if (!Number.isFinite(value.timestamp) || !Array.isArray(value.clientState?.queries)) throw new Error("Invalid cache");
        value.clientState.queries = value.clientState.queries.filter(allowed);
        if (!active) return;
        try {
          await storage.setItem(key, JSON.stringify(value));
          legacyRemove(true);
        } catch { /* Retain readable data and the legacy copy if a write fails. */ }
        return value;
      } catch { await storage.removeItem(key); }
    }),
    removeClient: () => enqueue(() => storage.removeItem(key)),
  };
  return {
    options: {
      persister,
      maxAge: CACHE_MAX_AGE,
      buster: "group-content-v1",
      dehydrateOptions: {
        shouldDehydrateMutation: () => false,
        shouldDehydrateQuery: query => query.state.data !== undefined
          && ![401, 403, 404].includes(query.state.error?.status)
          && allowed(query),
      },
    },
    dispose() { active = false; legacyStores.forEach(legacy => { try { legacy.removeItem(key); } catch {} }); return persister.removeClient(); },
  };
}
